import { Link, useNavigate } from "react-router-dom";
import { Play, Plus, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useMyList } from "../context/MyListContext";
import { shareVideo } from "../lib/share";

export const VideoCard = ({ video, hideSave = false, progress = null }) => {
  const thumb = `https://i.ytimg.com/vi/${video.youtube_id}/hqdefault.jpg`;
  const { user } = useAuth();
  const { isSaved, toggle } = useMyList();
  const navigate = useNavigate();
  const saved = isSaved(video.id);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate("/login");
      return;
    }
    toggle(video.id).then((now) => {
      if (now === true) toast.success("Added to My List");
      else if (now === false) toast("Removed from My List");
    });
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    shareVideo(video);
  };

  return (
    <Link
      to={`/watch/${video.id}`}
      data-testid={`video-card-${video.id}`}
      className="group/card block w-full transition-transform duration-300 ease-out hover:scale-[1.05] hover:z-10"
    >
      <div className="relative aspect-video overflow-hidden bg-[#0A0A0F] border border-white/10 rounded-sm">
        {video.is_live ? (
          <span
            data-testid={`live-badge-${video.id}`}
            className="absolute top-2 left-2 z-20 inline-flex items-center gap-1 bg-[#FF2A2A] text-white text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
          </span>
        ) : null}
        <img
          src={thumb}
          alt={video.title}
          loading="lazy"
          className="w-full h-full object-cover opacity-85 group-hover/card:opacity-100 transition-opacity duration-300"
        />
        <button
          data-testid={`save-button-${video.id}`}
          onClick={handleSave}
          aria-label={saved ? "Remove from My List" : "Add to My List"}
          className={`absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-black/70 backdrop-blur border flex items-center justify-center transition-all duration-200 hover:bg-black/90 hover:scale-110 ${
            hideSave ? "hidden" : ""
          } ${
            saved
              ? "opacity-100 border-[#FF2A2A]"
              : "opacity-100 md:opacity-0 md:group-hover/card:opacity-100 border-white/25"
          }`}
        >
          {saved ? (
            <Check className="w-4 h-4 text-[#FF2A2A]" strokeWidth={3} />
          ) : (
            <Plus className="w-4 h-4 text-white" strokeWidth={2.5} />
          )}
        </button>
        <button
          data-testid={`share-button-${video.id}`}
          onClick={handleShare}
          aria-label="Share this show"
          className={`absolute top-2 z-20 w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/25 flex items-center justify-center transition-all duration-200 hover:bg-black/90 hover:scale-110 opacity-100 md:opacity-0 md:group-hover/card:opacity-100 ${
            hideSave ? "right-2" : "right-11"
          }`}
        >
          <Share2 className="w-4 h-4 text-white" strokeWidth={2.5} />
        </button>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 bg-[#FF2A2A] rounded-full flex items-center justify-center shadow-lg">
            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
          </div>
        </div>
        {video.duration ? (
          <span className="absolute bottom-2 right-2 text-xs tabular-nums bg-black/80 px-1.5 py-0.5 rounded-sm text-zinc-200">
            {video.duration}
          </span>
        ) : null}
        {progress ? (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-10" data-testid={`progress-bar-${video.id}`}>
            <div className="h-full bg-[#FF2A2A]" style={{ width: `${Math.min(100, Math.max(3, progress * 100))}%` }} />
          </div>
        ) : null}
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-3 opacity-0 group-hover/card:translate-y-0 group-hover/card:opacity-100 transition-all duration-300">
          <h4 className="text-sm font-semibold text-white line-clamp-2 leading-tight">{video.title}</h4>
          {video.source_handle ? (
            <p className="text-[11px] text-zinc-400 mt-1">@{video.source_handle}</p>
          ) : null}
        </div>
      </div>
      <div className="mt-2 group-hover/card:opacity-0 transition-opacity duration-200">
        <h4 className="text-sm font-medium text-white truncate">{video.title}</h4>
        <p className="text-xs text-zinc-500 uppercase tracking-wider mt-0.5">{video.category}</p>
      </div>
    </Link>
  );
};
